import { type FC, useState, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { isVimeoVideo } from 'dibs-image-utils/exports/useItemMediaItems.ts';
import { VideoContainer } from '../../respShared/PdpRespSharedHeroCarousel/CarouselItems/VideoContainer.tsx';
import { type CarouselItem } from './types.ts';

import { type VideoOverlay_viewer$key } from './__generated__/VideoOverlay_viewer.graphql.ts';

import styles from './VideoOverlay.module.css';

export const useVideoOverlay = ({
    carouselItems,
    activeSlide,
    previewIndex,
}: {
    carouselItems: CarouselItem[];
    activeSlide: number;
    previewIndex: number | null;
}): {
    videoItem: CarouselItem | null;
    videoMounted: boolean;
    showVideo: boolean;
    isVideoActive: boolean;
    isOtherThumbHovered: boolean;
    videoPaused: boolean;
} => {
    const videoIndex = carouselItems.findIndex(ci => ci.__type === 'video');
    const videoItem = videoIndex >= 0 ? carouselItems[videoIndex] : null;
    const isVideoActive = activeSlide - 1 === videoIndex;
    const isVideoHovered = previewIndex === videoIndex;
    const showVideo = isVideoHovered || isVideoActive;

    const [videoMounted, setVideoMounted] = useState(false);
    useEffect(() => {
        if (showVideo && !videoMounted) {
            setVideoMounted(true);
        }
    }, [showVideo, videoMounted]);

    const isOtherThumbHovered = previewIndex !== null && previewIndex !== videoIndex;
    const videoPaused = isVideoActive && isOtherThumbHovered;

    return { videoItem, videoMounted, showVideo, isVideoActive, isOtherThumbHovered, videoPaused };
};

export const VideoOverlay: FC<{
    viewer: VideoOverlay_viewer$key;
    videoItem: CarouselItem | null;
    videoMounted: boolean;
    showVideo: boolean;
    isVideoActive: boolean;
    isOtherThumbHovered: boolean;
    videoPaused: boolean;
}> = ({
    viewer: viewerRef,
    videoItem,
    videoMounted,
    showVideo,
    isVideoActive,
    isOtherThumbHovered,
    videoPaused,
}) => {
    const viewer = useFragment(
        graphql`
            fragment VideoOverlay_viewer on Viewer {
                vimeoItemsVideoHLSPlayerFF: featureFlag(feature: "vimeoItemsVideoHLSPlayer")
            }
        `,
        viewerRef
    );

    if (!videoMounted || videoItem?.__type !== 'video') {
        return null;
    }

    const vimeoItemsVideoHLSPlayerFF = !!viewer?.vimeoItemsVideoHLSPlayerFF;
    return (
        <div
            className={classnames(
                dibsCss.absolute,
                dibsCss.inset0,
                dibsCss.zBump,
                dibsCss.bgMoonstone,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter,
                (!showVideo || isOtherThumbHovered) && dibsCss.invisible,
                showVideo && !isVideoActive && styles.previewOverlay,
                { [dibsCss.flexCol]: vimeoItemsVideoHLSPlayerFF }
            )}
        >
            {isVimeoVideo(videoItem) ? (
                <VideoContainer
                    vimeoItemsVideoHLSPlayerFF={vimeoItemsVideoHLSPlayerFF}
                    videoVimeo={videoItem}
                    videoUrl={videoItem.videoUrl?.link || ''}
                    heightOffset={0}
                    paused={videoPaused || (vimeoItemsVideoHLSPlayerFF && !showVideo)}
                />
            ) : (
                <VideoContainer
                    vimeoItemsVideoHLSPlayerFF={vimeoItemsVideoHLSPlayerFF}
                    videoUrl={videoItem.url || ''}
                    heightOffset={0}
                    paused={videoPaused || !showVideo}
                />
            )}
        </div>
    );
};
